import { type CSSProperties, type PointerEvent as ReactPointerEvent, useState } from "react";

import { categoricalSlotVar } from "./chart-color";
import { formatCompactCount } from "./chart-format";
import { arcPath } from "./chart-geometry";
import { ChartHoverTooltip } from "./chart-hover-tooltip";
import { ChartLegend } from "./chart-legend";
import type { DonutSlice, ValueFormatter } from "./chart-types";
import { cn } from "./cn";
import { foldPieSlicesToThree } from "./donut-fold";
import "./chart-svg-shared.css";
import "./donut-chart.css";

export interface DonutChartProps {
  title: string;
  /** Raw, pre-fold categories. DonutChart folds to the top 3 by value + one
   *  "Other" slice itself (see donut-fold.ts) — a caller never needs to, and
   *  passing an already-folded list is safe (folding is idempotent). */
  slices: DonutSlice[];
  formatValue?: ValueFormatter;
  /** Caption suffix after the total figure, e.g. "projects" or "in 24h". */
  totalLabel?: string;
  /** Override the auto-built <svg role="img"> accessible name. */
  ariaLabel?: string;
  /** Opens the drilldown behind this chart's total. */
  onOpen?: () => void;
  /** Opt-in floating tooltip on a segment hover (mouse only) — see
   *  BarChart's `hoverTooltip` doc for why this stays opt-in rather than
   *  default-on. Each segment's native `<title>` is unchanged either way. */
  hoverTooltip?: boolean;
  className?: string;
}

// A visible seam between adjacent segments (the dataviz "2px gap" mark spec,
// translated to degrees rather than pixels since a stroke-based ring has no
// per-segment fill boundary to put a literal gap on). Applied as a half-gap
// inset on BOTH ends of every segment's sweep, including a lone 100% slice —
// which doubles as the fix for the SVG arc command's degenerate case: a
// mathematically exact 360deg sweep has coincident start/end points and
// renders nothing, so the inset keeps every sweep strictly under 360.
const GAP_DEG = 3;
const MIN_SWEEP_DEG = 1;

const CX = 70;
const CY = 70;
const R = 52;
const STROKE_WIDTH = 22;

/**
 * Donut ring over an arbitrary category list. Folds to top 3 + Other
 * internally and unconditionally (see donut-fold.ts for the 3-slot all-pairs
 * cap), which is why color is not a prop. Each slice is a stroked arc from
 * `arcPath`, so a lone slice and the full circle need no special case beyond
 * the gap inset.
 */
export function DonutChart({
  title,
  slices,
  formatValue = formatCompactCount,
  totalLabel,
  ariaLabel,
  onOpen,
  hoverTooltip = false,
  className,
}: DonutChartProps) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [hoverAnchor, setHoverAnchor] = useState<{ x: number; y: number } | null>(null);

  const folded = foldPieSlicesToThree(slices);
  const total = folded.reduce((sum, s) => sum + s.value, 0);
  const resolvedAriaLabel =
    ariaLabel ?? `${title}, ${formatValue(total)}${totalLabel ? ` ${totalLabel}` : ""}`;

  let cursor = 0;
  const segments = folded.map((slice, index) => {
    const share = total > 0 ? slice.value / total : 0;
    const fromDeg = cursor * 360;
    const toDegRaw = (cursor + share) * 360;
    cursor += share;
    const halfGap = GAP_DEG / 2;
    const from = fromDeg + halfGap;
    const to = Math.max(toDegRaw - halfGap, from + MIN_SWEEP_DEG);
    const color = slice.isOther ? "var(--rc-chart-other)" : categoricalSlotVar(index);
    const pct = total > 0 ? Math.round(share * 100) : 0;
    return { slice, color, path: arcPath(CX, CY, R, from, to), pct };
  });
  const hoveredSegment = hoverIndex !== null ? segments[hoverIndex] : undefined;

  const onSegmentPointerMove = (index: number) => (event: ReactPointerEvent<SVGPathElement>) => {
    if (!hoverTooltip || event.pointerType !== "mouse") return;
    setHoverIndex(index);
    setHoverAnchor({ x: event.clientX, y: event.clientY });
  };
  const onSegmentPointerLeave = (index: number) => (event: ReactPointerEvent<SVGPathElement>) => {
    if (!hoverTooltip || event.pointerType !== "mouse") return;
    setHoverIndex((prev) => (prev === index ? null : prev));
  };

  return (
    <figure className={cn("rc-chart", "rc-donut", className)}>
      <figcaption className="rc-chart__caption">
        <span className="rc-chart__title">{title}</span>
        {onOpen ? (
          <button
            type="button"
            className="rc-chart__total"
            onClick={onOpen}
            title={`See the rows behind ${title.toLowerCase()}`}
          >
            {formatValue(total)}
            {totalLabel ? ` ${totalLabel}` : ""}
          </button>
        ) : (
          <span className="rc-chart__total">
            {formatValue(total)}
            {totalLabel ? ` ${totalLabel}` : ""}
          </span>
        )}
      </figcaption>
      <svg
        className="rc-donut__svg"
        viewBox="0 0 140 140"
        role="img"
        aria-label={resolvedAriaLabel}
      >
        <circle className="rc-donut__track" cx={CX} cy={CY} r={R} strokeWidth={STROKE_WIDTH} />
        {segments.map(({ slice, color, path, pct }, index) =>
          slice.value > 0 ? (
            <path
              key={slice.key}
              className="rc-donut__segment"
              style={{ stroke: color } as CSSProperties}
              strokeWidth={STROKE_WIDTH}
              d={path}
              onPointerMove={onSegmentPointerMove(index)}
              onPointerLeave={onSegmentPointerLeave(index)}
            >
              <title>{`${slice.label}: ${formatValue(slice.value)} (${pct}%)`}</title>
            </path>
          ) : null,
        )}
        <text className="rc-donut__value" x={CX} y={CY + 6} textAnchor="middle">
          {formatValue(total)}
        </text>
      </svg>
      {hoveredSegment && hoverAnchor ? (
        <ChartHoverTooltip anchor={hoverAnchor}>
          <span className="rc-chart-hover-tooltip__row">
            <span
              className="rc-chart-hover-tooltip__swatch"
              aria-hidden="true"
              style={{ background: hoveredSegment.color }}
            />
            <span className="rc-chart-hover-tooltip__label">{hoveredSegment.slice.label}</span>
            <span className="rc-chart-hover-tooltip__value">
              {formatValue(hoveredSegment.slice.value)} ({hoveredSegment.pct}%)
            </span>
          </span>
        </ChartHoverTooltip>
      ) : null}
      {folded.length > 1 ? (
        <ChartLegend
          series={folded.map((s) => ({ key: s.key, label: s.label }))}
          colors={new Map(segments.map(({ slice, color }) => [slice.key, color]))}
        />
      ) : null}
    </figure>
  );
}
